import { useCallback, useEffect, useRef, useState } from 'react';
import { InputField } from '@clay-run/glaze/input-field';
import { SelectField } from '@clay-run/glaze/select-field';
import { TextareaField } from '@clay-run/glaze/textarea-field';
import type { SenderOption } from '@shared/flow/enrollment';
import type { BaseCopyDraftStore } from './EnrollQueueContext';
import { playConfigKey, useStagedBaseCopy, useStagedBaseSender } from './state/staged-play-config';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';

/**
 * Play-level Global-config editor — Sender + base subject/body, and the ONE
 * editor in the queue that never saves
 * at ALL (design-fidelity plan D8 Part B; supersedes both the original
 * "explicit Save and override button" version and the round-4 debounced
 * autosave that was planned to replace it).
 *
 * Every other copy field in the queue writes through the per-keystroke
 * debounced PATCH (`EnrollQueueContext.autosavePatch`). This one writes to
 * `draftStore.setDraft` and nothing else: the play-level base copy is
 * REP-LOCAL CLIENT STATE (Spencer, 2026-09-01 round 5 — "we are not writing
 * back to any dbs… only the rep and its contacts and their state stores the
 * staged copy while in queue"). That store write IS the whole write path —
 * zustand `persist` rides it through to localStorage, so the staged text
 * survives a card collapse, a rail switch, a grouping switch, a reload and a
 * tab close (2026-09-02 follow-up: "if someone closes their tab they should
 * still be able to see the overridden changes") — and it is structurally
 * incapable of reaching another rep or any server. There is no button, no
 * debounce, no error state and no `Alert`: nothing can fail because nothing
 * round-trips. At Send, the staged value rides the POST's `staged` wire field
 * (`EnrollQueueContext.buildStagedSnapshot`) and the server resolves it onto
 * each non-overridden item's own `subject_draft`/`body_draft` at claim time
 * (terra-ohe.7, F1 U2/U6) — never a client-side pre-POST PATCH.
 *
 * ── D24 (qa-round3 plan, fix 2): a LOCAL BUFFER in front of that write ──
 * The subject/body fields keep an uncommitted local mirror and commit it to
 * the store on a ~200ms idle debounce, on blur, or on unmount. Before this,
 * the fields were 100% store-controlled with no local state at all, so the
 * caret could not advance until the store write, `persist`'s synchronous
 * `localStorage.setItem`, and the whole downstream render had committed.
 *
 * What this changes for a reader of this file: "the store write IS the whole
 * write path" is still exactly true (there is still no endpoint, no network,
 * no error state, nothing that can fail) — but it is no longer SYNCHRONOUS
 * with the keystroke. Anything asserting on the store after typing must
 * commit first; blur is the deterministic way to do that, and it is also why
 * Send is safe: blur fires before the Send button's click, so the buffered
 * text is in the store before `buildStagedSnapshot` reads it. The unmount
 * flush covers card collapse / rail switch / grouping switch, and the
 * "live-follow is instant" language in D8's browser-QA notes is now
 * "live-follow within ~200ms".
 *
 * The SENDER field has no buffer: it is a select, so there is one discrete
 * write per pick and nothing to coalesce.
 *
 * The buffer is a plain `useState` holding `null` for "nothing uncommitted,
 * render the store" — deliberately NOT a `useState` + reseed effect (the
 * shape CLAUDE.md's "Draft State Under SSE" rule forbids) and deliberately
 * not `useDraftState` either: there is no reseed effect anywhere here,
 * because the rendered value is DERIVED (`buffer ?? staged ?? campaign`) and
 * the buffer clears at commit. An external change — a cross-tab rehydrate,
 * the post-send prune, a reset — therefore shows through as soon as the rep
 * stops typing, with no effect to get stale.
 *
 * ── D24 fix 1: this component subscribes to the store itself ─────────────
 * `draftStore`'s reads are non-reactive `getState()` calls now (that is what
 * makes the context value stable across a keystroke), so the staged values
 * this component DISPLAYS come from narrow per-play subscriptions
 * (`useStagedBaseCopy`/`useStagedBaseSender`) rather than from the facade.
 * Writes still go through the facade, unchanged.
 *
 * `readOnly` still disables the fields (the queue's impersonation mirror) —
 * a read-only session shouldn't be staging copy it can't send.
 *
 * The fields seed from the play's CAMPAIGN copy (`PlayOption.invite_subject`/
 * `invite_body`) whenever this play has no staged entry — "clean" IS "no
 * entry", not "entry equal to seed", so there is no reseed effect anywhere
 * and SSE/plays-refetch churn can never wipe or stale-pin the editor. The
 * seeds passed in are deliberately the WIRE values, not the staged overlay:
 * the store holds the dirty layer, and passing the overlay back in would
 * double-apply it.
 *
 * No section heading of its own: the card's "Bulk Editor" header
 * (`PlayQueueCard`) is the prototype's structure
 * (contacts-bulk-send-prototype.html:872-880) and this component sits
 * directly under it.
 *
 * ── The Sender field (D12 point 2) ───────────────────────────────────────
 * Sender leads the section, and unlike Base subject/body it renders for
 * EVERY play type — `hasEditableCopy` is now an INTERNAL gate on the two
 * copy fields rather than a gate on mounting this component at all. That is
 * item 14's literal ask: a single-option play (generate_message, warm_intro,
 * gong_flow) must show "Send as myself" visibly selected instead of showing
 * the rep nothing at all.
 *
 * Its clean value is the play's ROLE-DERIVED default (`defaultSender`,
 * `SendersResponse.default_sender`), never a derivation over the rows: there
 * is deliberately no "Mixed" state, because per-row divergence is what the
 * Overridden badge is for. The placeholder therefore appears only in the
 * genuinely-null case (a role-null MEMBER/REVOPS rep on event_invite with
 * nothing staged).
 *
 * Writes go to `draftStore.setBaseSender` and nowhere else — same
 * no-button/no-network/no-debounce path as the subject/body fields above,
 * and Send materializes it onto each non-touched row's own `sender` column.
 *
 * Glaze boundary: this component is built end to end from real
 * `@clay-run/glaze` components — props verified against the Storybook MCP
 * server plus `node_modules/@clay-run/glaze/dist` type definitions. Import
 * style (per-component subpaths, `*Field` wrappers at their own `-field`
 * subpath) follows docs/glaze/glaze-usage-patterns-terra.md §2.
 */

export interface BaseCopyDraft {
  subject: string;
  body: string;
}

/**
 * D24 fix 2 — how long the buffered subject/body waits before it lands in the
 * store. Long enough to coalesce a burst of typing, short enough that the
 * open-editor live-follow still reads as immediate to a rep (D8's browser-QA
 * line, restated: "within ~200ms" rather than "instant"). Blur and unmount
 * both flush ahead of it, so nothing depends on the timer for correctness.
 *
 * KNOWN GAP, not fixed here (Codex adversarial follow-up) — React's effect
 * cleanup (what `flushPending` hooks into on unmount) only runs when the
 * REACT TREE unmounts; a browser tab close or hard reload doesn't unmount
 * anything, it just ends the process. Typing and closing/reloading the tab
 * within this ~200ms window loses that last edit. Deliberately NOT closed
 * with a `beforeunload` listener here: `EnrollQueueContext.tsx` documents a
 * standing decision (2026-07-22, Spencer QA) that a queue-wide `beforeunload`
 * guard was removed for blocking ordinary tab closes, and the one narrow
 * exception added since (Gong step-overrides, plan 2026-08-19-004 D3) is a
 * user-facing confirm dialog for a much larger unsaved-work case (a rep's
 * entire rewritten email sequence), not a silent 200ms buffer. Adding a new
 * `beforeunload` hook to THIS file — even a silent flush, no dialog — is a
 * call on that same sensitive surface and stays Spencer's, not made
 * unilaterally under this fix's scope.
 */
const BASE_COPY_COMMIT_MS = 200;

export function BaseCopyEditor({
  playTypeId,
  playInstanceRef,
  playName,
  effectiveSubject,
  effectiveBody,
  senders,
  defaultSender,
  hasEditableCopy,
  copyRequired,
  readOnly,
  draftStore,
}: {
  playTypeId: number;
  playInstanceRef: string;
  /** Used only for the fields' accessible names — one card per play on screen. */
  playName: string;
  /** The play's campaign copy from the catalog (`PlayOption.invite_subject`). */
  effectiveSubject: string | null;
  effectiveBody: string | null;
  /** D12 — `EnrollQueueContext.sendersFor(playTypeId)`; the play type's own picker list. */
  senders: SenderOption[];
  /** D12 — `EnrollQueueContext.defaultSenderFor(playTypeId)`; the ROLE-derived
   *  default, this field's clean value. `null` for a role-null rep. */
  defaultSender: string | null;
  /**
   * D12 — gates the two COPY fields only (this component now mounts for
   * every play type so the Sender field is always visible). A play that
   * resolves its copy server-side (generate_message, warm_intro) has no base
   * copy to override.
   */
  hasEditableCopy: boolean;
  /**
   * See `PlayOption.copy_required`'s doc comment. Only meaningful alongside
   * `hasEditableCopy: true`; gates the required-asterisk on the Base
   * subject/body labels below (event_invite: true; warm_intro: false — a
   * blank draft falls back to a template, so a star would be wrong there).
   */
  copyRequired: boolean;
  readOnly: boolean;
  /** `EnrollQueueContext.baseCopyDraftStore` — see this component's own doc
   *  comment for why a store write is the entire write path. */
  draftStore: BaseCopyDraftStore;
}) {
  // Store-only-while-dirty, derive-when-clean: a play with no entry in the
  // store renders the wire's campaign copy directly. D24 fix 1 — read via a
  // narrow subscription to THIS play's staged copy, so another play's staging
  // (or this identity's sender/touched writes) never re-renders this editor.
  const playKey = playConfigKey(playTypeId, playInstanceRef);
  const staged = useStagedBaseCopy(draftStore.identity, playKey);
  const effective = { subject: effectiveSubject ?? '', body: effectiveBody ?? '' };
  const committed = staged ?? effective;
  // Same "store while staged, derive when clean" rule for the sender, with
  // the role-derived default as the clean value. No buffer — see the doc
  // comment: one discrete write per pick.
  const sender = useStagedBaseSender(draftStore.identity, playKey) ?? defaultSender ?? '';

  // ── D24 fix 2 — the uncommitted local buffer ────────────────────────────
  // `null` means "nothing uncommitted, render the store". ONE buffer for the
  // PAIR rather than one per field: the store entry is a `{subject, body}`
  // pair, and two independent buffers would each have to spread the OTHER
  // field off the store — which a buffered edit has not reached yet, so a
  // subject edit followed by a body edit would silently drop the subject.
  const [buffer, setBuffer] = useState<BaseCopyDraft | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * The exact commit closure for whatever is currently buffered, captured at
   * SCHEDULE time (it holds that keystroke's value, play identity and
   * `draftStore`). Captured rather than rebuilt at flush time so a flush can
   * never write the pending text under a play key the component has since
   * re-rendered onto.
   */
  const pendingCommitRef = useRef<(() => void) | null>(null);
  // StrictMode: reset at the TOP of the mount effect, not only in cleanup —
  // a cleanup-only flag stays stuck `false` after dev's setup→cleanup→setup
  // double-invoke (CLAUDE.md "StrictMode-Resilient Effects").
  const mountedRef = useRef(true);

  /** Land the buffered text now (blur / unmount / the timer beating them). */
  const flushPending = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const commit = pendingCommitRef.current;
    pendingCommitRef.current = null;
    commit?.();
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Card collapse, rail switch, grouping switch, dock close — D8's
      // survival guarantee is that staged text outlives all four, so the
      // buffer must land rather than unmount with the component.
      flushPending();
    };
  }, [flushPending]);

  const draft = buffer ?? committed;

  const bufferEdit = (next: BaseCopyDraft) => {
    setBuffer(next);
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    const commit = () => {
      timerRef.current = null;
      pendingCommitRef.current = null;
      // Clearing the buffer hands the field back to the store, which now
      // holds this exact value — so there is no flicker, and an EXTERNAL
      // change (cross-tab rehydrate, post-send prune, reset) shows through
      // again the moment the rep stops typing.
      if (mountedRef.current) setBuffer(null);
      draftStore.setDraft(playTypeId, playInstanceRef, next);
    };
    pendingCommitRef.current = commit;
    timerRef.current = setTimeout(commit, BASE_COPY_COMMIT_MS);
  };
  // The per-row Sender field's own precedent (`ItemFields`): a value the live
  // list no longer contains (a staged pick from a session before the roster
  // changed, or a role default whose option was dropped) stays selectable as
  // a synthetic extra item, so the field never renders blank while a value
  // IS set.
  const senderItems = sender && !senders.some(s => s.value === sender)
    ? [{ value: sender, label: sender }, ...senders]
    : senders;

  // D20 (Option A, recommended) — the helper text no longer explains the
  // blank-falls-back-to-campaign-copy rule in prose; each field's own
  // placeholder labels it right where it happens, and the placeholder
  // disappears the moment the rep types. `effectiveSubject`/`effectiveBody`
  // (the wire's campaign copy) are what the placeholder is naming, so when
  // either is null there IS no campaign copy to fall back to — the
  // placeholder must say that instead of claiming a fallback that doesn't
  // exist (D20's null-campaign-copy variant).
  const subjectPlaceholder = effectiveSubject == null ? 'No campaign copy — enter a subject' : 'Using campaign subject';
  const bodyPlaceholder = effectiveBody == null ? 'No campaign copy — enter a body' : 'Using campaign copy';

  return (
    <div className="flex flex-col gap-2">
      {/* `title`/`div` wrapper omitted deliberately — unlike `ItemFields`'
          selects there is no locked-with-a-reason state here: this field is
          either editable or the whole card is read-only (impersonation),
          which `disabled` already conveys. */}
      {/* Sender is required for EVERY play type (it's rendered
          unconditionally, right here, for every play — see
          `EnrollmentItemEditor.tsx`'s "every play type has a sender"
          comment), so its label always carries the required-asterisk,
          unconditionally on `hasEditableCopy`/`copyRequired`. Token-only
          (`text-content-danger`, no raw hex, no mode-variant prefix) —
          rail-glaze-parity (U7) dropped this file's prior
          mode-variant-prefixed override. */}
      <SelectField
        label={<>Sender <span className="text-content-danger">*</span></>}
        aria-label={`Sender for ${playName}`}
        items={senderItems}
        value={sender}
        onValueChange={(value: string | null) => draftStore.setBaseSender(playTypeId, playInstanceRef, value ?? '')}
        placeholder="Select sender…"
        disabled={readOnly}
        size="sm"
      />
      {hasEditableCopy && (
        <>
          {/* The required-asterisk only appears when `copyRequired`
              (event_invite: its Subject/Copy are genuinely required to
              send). `warm_intro` has `hasEditableCopy: true` but
              `copyRequired: false` — a blank draft there legitimately
              falls back to a server-rendered template at send time, so a
              star would be actively wrong. */}
          <InputField
            {...AUTOFILL_IGNORE_PROPS}
            label={copyRequired ? <>Base subject <span className="text-content-danger">*</span></> : 'Base subject'}
            aria-label={`Base subject for ${playName}`}
            value={draft.subject}
            onValueChange={(value: string) => bufferEdit({ ...draft, subject: value })}
            onBlur={flushPending}
            placeholder={subjectPlaceholder}
            disabled={readOnly}
            size="sm"
          />
          <TextareaField
            {...AUTOFILL_IGNORE_PROPS}
            label={copyRequired ? <>Base body <span className="text-content-danger">*</span></> : 'Base body'}
            aria-label={`Base body for ${playName}`}
            rows={4}
            value={draft.body}
            onValueChange={(value: string) => bufferEdit({ ...draft, body: value })}
            onBlur={flushPending}
            placeholder={bodyPlaceholder}
            disabled={readOnly}
            size="sm"
          />
        </>
      )}
    </div>
  );
}
